import { normalizeHeader, type Matrix } from '../ganttpro/matrix-cells';

/**
 * O cabeçalho das duas abas da planilha de itens de compra.
 *
 * A planilha gerada fora do sistema escreve o cabeçalho em `snake_case` (`vai_para_sistema`),
 * e quem a abre no Excel e a refaz à mão escreve o rótulo humano (`Vai para o sistema`,
 * `Grupo (tema)`). Os dois têm de valer — por isso a comparação é por uma **chave** em que
 * sublinhado, barra, parêntese, ponto, dois-pontos e hífen viram espaço, além do que
 * `normalizeHeader` já faz (sem acento, sem caixa, espaço colapsado).
 */

/** `"Grupo (tema)"` e `"grupo_tema"` dão a mesma chave: `"grupo tema"`. */
export function headerKey(value: unknown): string {
  if (typeof value === 'string') return normalizeHeader(value.replace(/[_/().:\-–—]+/g, ' '));
  return normalizeHeader(value);
}

/** Uma coluna exigida: o nome que a mensagem de erro mostra e as grafias aceitas (já em chave). */
export interface RequiredColumn {
  readonly label: string;
  readonly spellings: readonly string[];
}

export interface HeaderMatch {
  /** Posição da linha no vetor (a linha do Excel é esta mais um). */
  readonly rowIndex: number;
  /** Chave do cabeçalho → índice da coluna (a primeira que aparece). */
  readonly columns: ReadonlyMap<string, number>;
}

export type HeaderSearch =
  | { readonly ok: true; readonly header: HeaderMatch }
  | {
      readonly ok: false;
      /**
       * A linha que mais se parece com o cabeçalho (metade das colunas exigidas ou mais), e o
       * que falta nela. `undefined` quando nenhuma linha chega perto.
       */
      readonly nearest:
        { readonly rowNumber: number; readonly missing: readonly string[] } | undefined;
    };

/**
 * A primeira linha, dentro do limite, que tem **todas** as colunas exigidas. Quando nenhuma
 * tem, devolve a mais próxima — "faltam `edt_do_item` e `metodo`" ajuda mais que "não achei".
 */
export function findHeader(
  matrix: Matrix,
  required: readonly RequiredColumn[],
  scanLimit: number,
): HeaderSearch {
  const last = Math.min(matrix.length, scanLimit);
  let nearest: { rowNumber: number; missing: string[]; matched: number } | undefined;

  for (let rowIndex = 0; rowIndex < last; rowIndex += 1) {
    const columns = new Map<string, number>();
    (matrix[rowIndex] ?? []).forEach((cell, index) => {
      const key = headerKey(cell);
      if (key !== '' && !columns.has(key)) columns.set(key, index);
    });

    const missing = required
      .filter((column) => !column.spellings.some((spelling) => columns.has(spelling)))
      .map((column) => column.label);
    if (missing.length === 0) return { ok: true, header: { rowIndex, columns } };

    const matched = required.length - missing.length;
    if (matched * 2 >= required.length && (nearest === undefined || matched > nearest.matched)) {
      nearest = { rowNumber: rowIndex + 1, missing, matched };
    }
  }

  return {
    ok: false,
    nearest:
      nearest === undefined
        ? undefined
        : { rowNumber: nearest.rowNumber, missing: nearest.missing },
  };
}
